/**
 * Figure outline style (setting `figureOutline`, figureOutline.setting.ts): 0 = the dark ink outline (today's look),
 * 2 = no outline at all (the user's 10-11 look test), 1 = the scene's outline: each outline pixel takes the line tone of the palette colour it borders (a darker step of
 * its own ramp, one step lighter on the lit upper-right side), as the selective outline of the scenery does
 * (truepx/clean.ts, same palette).
 *
 * Like the density and the true-pixel figure flag it is fixed for the page's life: figure frames live in worker
 * replies, the frame store, the bake and the atlas pages, so a change applies after a reload (the frame store's stamp
 * carries the mode, so each mode keeps its own frames). Workers get it in their first message.
 * Made once where the art is made (rig/finish.ts, the beasts), never per frame drawn.
 */
// No imports: the figure workers and the settings file load this.
let mode = 0;
let started = false;
let source: (() => number | undefined) | null = null;

function ensure(): void {
  if (started || !source) return;
  const v = source();
  if (v === undefined) return;
  started = true;
  mode = v === 1 || v === 2 ? v : 0;
}

/** Where the page's saved choice is read from (undefined until the settings are loaded). */
export function registerFigureOutlineSource(f: () => number | undefined): void {
  source = f;
}

/** True when figures are outlined in the scene's style. */
export function sceneOutlineOn(): boolean {
  ensure();
  return mode === 1;
}

/** True when figures get no outline at all. */
export function noOutlineOn(): boolean {
  ensure();
  return mode === 2;
}

/** The mode's tag for caches ('' for the default ink outline). */
export function figureOutlineTag(): string {
  ensure();
  return mode === 1 ? '-sceneout' : mode === 2 ? '-noout' : '';
}

/** What the main thread tells a new figure worker. */
export const figureOutlineMessage = (): { figureOutline: number } => {
  ensure();
  return { figureOutline: mode };
};

/** Takes the mode from a `figureOutlineMessage` (in a worker, or a tool); true if the message was one. */
export function takeFigureOutline(data: unknown): boolean {
  if (!data || typeof data !== 'object' || !('figureOutline' in data) || 'id' in data) return false;
  started = true;
  const v = (data as { figureOutline: unknown }).figureOutline;
  mode = v === 1 || v === 2 ? v : 0;
  return true;
}

/** The mode for tools and tests that draw figures without the settings. */
export function setFigureOutline(v: 0 | 1 | 2): void {
  started = true;
  mode = v;
}

